import React from 'react';
import { Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { Home, ClipboardList, History, MapPin, Wallet, User } from 'lucide-react';
import PhoneFrame from '../customer/PhoneFrame';
import { DriverWelcome, DriverLogin, DriverRegister, DriverPending, DriverHome, DriverOrderDetail, DriverNavigate, DriverOrders, DriverMap, DriverEarnings, DriverProfile, DriverNotifications } from './DriverScreens';
import { DriverHistory } from './DriverHistory';
import { useApp } from '../context/AppContext';
import { useDriverLocation } from '../hooks/useDriverLocation';
import { OrderChat } from '../components/OrderChat';
import { registerPush } from '../lib/native';

const TABS = [
  { to: '/driver/home', label: 'Inicio', icon: Home },
  { to: '/driver/orders', label: 'Mis pedidos', icon: ClipboardList },
  { to: '/driver/history', label: 'Historial', icon: History },
  { to: '/driver/map', label: 'Mapa', icon: MapPin },
  { to: '/driver/earnings', label: 'Ganancias', icon: Wallet },
  { to: '/driver/profile', label: 'Perfil', icon: User },
];
const NAV_PATHS = TABS.map((t) => t.to);

const RequireDriver = ({ children }) => {
  const { driver, driverRecord } = useApp();
  if (!driver) return <Navigate to="/driver/login" replace />;
  if ((driverRecord || driver).approval !== 'approved') return <Navigate to="/driver/pending" replace />;
  return children;
};

const DriverApp = () => {
  const { pathname } = useLocation();
  const { driver, driverRecord, activeJobs } = useApp();
  const approved = (driverRecord || driver)?.approval === 'approved';
  const fem = (driverRecord || driver)?.gender === 'mujer';
  useDriverLocation(approved ? driver?.id : null, activeJobs[0]);
  React.useEffect(() => { if (approved && driver?.id) registerPush({ role: 'driver', userId: driver.id }); }, [approved, driver?.id]);
  const showNav = NAV_PATHS.some((p) => pathname === p || pathname === `${p}/`);
  return (
    <PhoneFrame className={fem ? 'theme-fem' : ''} tagline="Sé parte del movimiento" subtitle="Conecta, Recoge, Entrega, Gana" scriptSub="Tu ciudad, más oportunidades" extraLogo={<span className="text-2xl font-semibold text-[#1E2130] -ml-3">Driver App</span>}>
      <Routes>
        <Route index element={<DriverWelcome />} />
        <Route path="login" element={<DriverLogin />} />
        <Route path="register" element={<DriverRegister />} />
        <Route path="pending" element={<DriverPending />} />
        <Route path="home" element={<RequireDriver><DriverHome /></RequireDriver>} />
        <Route path="order/:id" element={<RequireDriver><DriverOrderDetail /></RequireDriver>} />
        <Route path="chat/:id" element={<RequireDriver><OrderChat role="driver" /></RequireDriver>} />
        <Route path="navigate/:id" element={<RequireDriver><DriverNavigate /></RequireDriver>} />
        <Route path="orders" element={<RequireDriver><DriverOrders /></RequireDriver>} />
        <Route path="history" element={<RequireDriver><DriverHistory /></RequireDriver>} />
        <Route path="map" element={<RequireDriver><DriverMap /></RequireDriver>} />
        <Route path="earnings" element={<RequireDriver><DriverEarnings /></RequireDriver>} />
        <Route path="profile" element={<RequireDriver><DriverProfile /></RequireDriver>} />
        <Route path="notifications" element={<RequireDriver><DriverNotifications /></RequireDriver>} />
        <Route path="*" element={<Navigate to="/driver" replace />} />
      </Routes>
      {showNav && (
        <nav className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around px-1 pt-2 pb-5 md:pb-4 z-30" style={{ paddingBottom: 'calc(1.25rem + var(--safe-bottom))' }}>
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} data-testid={`dtab-${t.label.toLowerCase().replace(/\s+/g, '-')}`} className={({ isActive }) => `flex flex-col items-center gap-1 text-[9px] font-medium w-[56px] whitespace-nowrap ${isActive ? 'text-[#EE3B36]' : 'text-gray-500'}`}>
              {({ isActive }) => (<><t.icon size={20} strokeWidth={isActive ? 2.4 : 2} className={isActive ? 'fill-[#EE3B36]/15' : ''} />{t.label}</>)}
            </NavLink>
          ))}
        </nav>
      )}
    </PhoneFrame>
  );
};

export default DriverApp;
